import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Tag, Modal, message, Tooltip, Card, Dropdown, Empty, Form, Input, Select, Col, MenuProps } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined, SyncOutlined, ExportOutlined, ImportOutlined, EditOutlined, CheckCircleOutlined, StopOutlined, EyeOutlined, MoreOutlined, PoweroffOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StoreGroup, StoreGroupStatus, SortField } from '../../types/storeGroup';
import StoreGroupService from '../../services/storeGroupService';
import FilterPanel from '../../components/common/FilterPanel';
import BatchActionToolbar from '../../components/common/BatchActionToolbar';
import { STORE_GROUP_STATUS_COLORS } from '../../mock/storeGroupData';
import HQDisplayGroupService from '../../services/hqDisplayGroupService';
import dayjs from 'dayjs';
import categoryService from '../../services/categoryService';
import { Planogram, PlanogramStatus } from "../../types/storeGroup";

const { confirm } = Modal;
const { Option } = Select;

const STATUS_COLORS: Record<PlanogramStatus, string> = {
  DRAFT: "orange",
  IN_REVIEW: "blue",
  APPROVED: "green",
  REJECTED: "red",
  ACTIVE: "green",
  ARCHIVED: "red",
};

// Status translation mapping
const getStatusTranslation = (status: PlanogramStatus, t: any): string => {
  const statusMap: Record<PlanogramStatus, string> = {
    DRAFT: t("storeGroup.benchmarkManagement.planogram.status.draft"),
    IN_REVIEW: t(
      "storeGroup.benchmarkManagement.planogram.status.pendingReview"
    ),
    APPROVED: t("storeGroup.benchmarkManagement.planogram.status.approved"),
    REJECTED: t("storeGroup.benchmarkManagement.planogram.status.rejected"),
    ACTIVE: t("storeGroup.benchmarkManagement.planogram.status.active"),
    ARCHIVED: t("storeGroup.benchmarkManagement.planogram.status.archived"),
  };
  return statusMap[status] || status;
};

const StoreGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [storeGroups, setStoreGroups] = useState<StoreGroup[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState({
    status: 'all' as StoreGroupStatus | 'all',
    keyword: '',
    displayGroupCode: undefined as string | undefined,
    mainDivisionCode: undefined as string | undefined, // 新增division过滤条件
  });
  const [sortField, setSortField] = useState<SortField>();
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  const [displayGroups, setDisplayGroups] = useState<{ code: string; name: string }[]>([]);
  const [displayGroupsLoading, setDisplayGroupsLoading] = useState<boolean>(false);
  const [divisions, setDivisions] = useState<{ value: string; label: string }[]>([]); // 新增divisions状态
  const [divisionsLoading, setDivisionsLoading] = useState<boolean>(false); // 新增divisionsLoading状态
  
  // 添加一个ref来跟踪是否是首次加载
  const isFirstLoad = useRef(true);
  // 添加一个ref来跟踪数据是否已经加载
  const isDataLoaded = useRef(false);

  const storeGroupService = StoreGroupService;
  const hqDisplayGroupService = HQDisplayGroupService;

  // 合并API调用，同时获取门店组列表和陈列组数据
  const fetchData = async (forceReloadDisplayGroups = false) => {
    // 如果数据已经加载且不是强制刷新，则跳过
    if (isDataLoaded.current && !forceReloadDisplayGroups) {
      // 只获取门店组数据
      await fetchStoreGroupsOnly();
      return;
    }
    
    setLoading(true);
    if (forceReloadDisplayGroups) {
      setDisplayGroupsLoading(true);
    }
    
    try {
      // 创建获取门店组数据的函数
      const fetchStoreGroupsPromise = async () => {
        // 处理filters，如果status为'all'则不传递status字段
        const processedFilters = { ...filters };
        if (processedFilters.status === 'all') {
          delete processedFilters.status;
        }
        
        // 修正参数：合并分页和排序参数
        return await storeGroupService.getStoreGroups(
          processedFilters,
          { 
            currentPage: current, 
            pageSize, 
            sortBy: sortField, 
            sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc' 
          }
        );
      };
      
      // 创建获取陈列组数据的函数（仅在首次加载或强制刷新时执行）
      const fetchDisplayGroupsPromise = async () => {
        return hqDisplayGroupService.getHQDisplayGroups(
          { status: 'ENABLE' },
          { currentPage: 1, pageSize: 1000 }
        );
      };
      
      // 创建获取事业部数据的函数（仅在首次加载时执行）
      const fetchDivisionsPromise = async () => {
        setDivisionsLoading(true);
        try {
          const response = await categoryService.listDivision();
          if (response.data) {
            setDivisions(response.data);
          }
        } catch (error) {
          console.error('Failed to fetch divisions:', error);
          message.error(t('storeGroup.fetchDivisionFailed'));
        } finally {
          setDivisionsLoading(false);
        }
      };
      
      let storeGroupsResponse;
      let displayGroupsResponse;
      
      // 如果是首次加载或需要强制刷新陈列组数据，则并行执行请求
      if (isFirstLoad.current || forceReloadDisplayGroups) {
        const promises = [fetchStoreGroupsPromise(), fetchDisplayGroupsPromise()];
        
        // 如果是首次加载，也获取事业部数据
        if (isFirstLoad.current) {
          promises.push(fetchDivisionsPromise() as any);
        }
        
        const results = await Promise.all(promises);
        [storeGroupsResponse, displayGroupsResponse] = results;
      } else {
        // 否则只获取门店组数据
        storeGroupsResponse = await fetchStoreGroupsPromise();
      }
      
      // 处理门店组列表响应
      if (storeGroupsResponse.data && storeGroupsResponse.data.tableData) {
        setStoreGroups(storeGroupsResponse.data.tableData);
        setTotal(storeGroupsResponse.data.paging.totalCount);
      } else {
        console.error('Invalid data format returned:', storeGroupsResponse);
        setStoreGroups([]);
        setTotal(0);
      }
      
      // 处理陈列组响应（如果有的话）
      if (displayGroupsResponse?.data?.tableData) {
        setDisplayGroups(displayGroupsResponse.data.tableData.map((group: any) => ({
          code: group.code,
          name: group.name,
        })));
      }
      
      // 标记数据已加载
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        isDataLoaded.current = true;
      }
    } catch (error) {
      message.error(t('storeGroup.fetchFailed'));
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setDisplayGroupsLoading(false);
    }
  };
  
  // 仅获取门店组数据的函数
  const fetchStoreGroupsOnly = async () => {
    setLoading(true);
    try {
      // 处理filters，如果status为'all'则不传递status字段
      const processedFilters = { ...filters };
      if (processedFilters.status === 'all') {
        delete processedFilters.status;
      }
      
      // 修正参数：合并分页和排序参数
      const response = await storeGroupService.getStoreGroups(
        processedFilters,
        { 
          currentPage: current, 
          pageSize, 
          sortBy: sortField, 
          sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc' 
        }
      );
      
      // 处理响应
      if (response.data && response.data.tableData) {
        setStoreGroups(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        console.error('Invalid data format returned:', response);
        setStoreGroups([]);
        setTotal(0);
      }
    } catch (error) {
      message.error(t('storeGroup.fetchFailed'));
      console.error('Failed to fetch store group list:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [current, pageSize, filters, sortField, sortOrder]);

  const handleTableChange = (
    pagination: any,
    _filters: any,
    sorter: any
  ) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  const handleFiltersChange = (values: any) => {
    const newFilters = {
      status: values.status,
      keyword: values.keyword,
      displayGroupCode: values.displayGroupCode,
      mainDivisionCode: values.mainDivisionCode // 新增division过滤条件
    };
    setFilters(newFilters);
    setCurrent(1); // 重置到第一页
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({
      status: 'all',
      keyword: '',
      displayGroupCode: undefined,
      mainDivisionCode: undefined, // 重置division过滤条件
    });
    setCurrent(1);
    setSortField('gmtCreate');
    setSortOrder('descend');
    return {}; // 返回空对象作为重置后的表单值
  };

  const handleCreate = () => {
    navigate('/store-group/create');
  };

  const handleEdit = (record: StoreGroup) => {
    navigate(`/store-group/${record.code}/edit`);
  };

  const handleView = (record: StoreGroup) => {
    navigate(`/store-group/${record.code}`);
  };

  const handleBenchmarkView = (record: StoreGroup) => {
    navigate(`/store-group/${record.code}/benchmark`);
  };

  const handleBatchStatusChange = (newStatus: StoreGroupStatus) => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('storeGroup.selectToUpdateStatus'));
      return;
    }

    // 将选中的code转换为id数组
    const selectedIds = selectedRowKeys.map(code => {
      const storeGroup = storeGroups.find(sg => sg.code === code);
      return storeGroup?.id;
    }).filter(Boolean) as string[];

    const action = newStatus === 'ENABLE' ? t('common.activate') : t('common.deactivate');
    Modal.confirm({
      title: t('common.confirmBatchStatusChange'),
      icon: <ExclamationCircleOutlined />,
      content: t('storeGroup.batchStatusChangeConfirm', { action, count: selectedRowKeys.length }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await storeGroupService.batchUpdateStatus(selectedIds, newStatus);
          message.success(t('storeGroup.batchStatusUpdateSuccess'));
          setSelectedRowKeys([]);
          fetchStoreGroupsOnly(); // 只刷新门店组数据
        } catch (error: any) {
          message.error(t('storeGroup.batchStatusUpdateFailed', { error: error.message }));
        }
      },
    });
  };

  const handleExportNielsen = (record: StoreGroup) => {
    confirm({
      title: t('storeGroup.exportConfirm'),
      icon: <ExportOutlined />,
      content: t('storeGroup.exportNielsenConfirm', { name: record.name }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const result = await storeGroupService.exportToNielsen(record.code);
          message.success(t('storeGroup.exportSuccess'));
          // 模拟下载文件
          const link = document.createElement('a');
          if (result.data) {
            link.href = result.data.downloadUrl;
            link.download = result.data.fileName;
          } else {
            message.error(t('storeGroup.exportDataFormatError'));
            return;
          }
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          fetchStoreGroupsOnly(); // 只刷新门店组数据以更新最后导出时间
        } catch (error: any) {
          message.error(t('storeGroup.exportFailed', { error: error.message }));
        }
      },
    });
  };

  // Get action menu items for each row
  const getActionMenuItems = (record: StoreGroup): MenuProps['items'] => [
    {
      key: 'export',
      icon: <ExportOutlined />,
      label: t('storeGroup.exportNielsen'),
      onClick: () => handleExportNielsen(record)
    }
  ];

  const columns = [
    {
      title: t('storeGroup.code'),
      dataIndex: 'code',
      key: 'code',
      fixed: 'left' as const,
      // sorter: true,
      // sortOrder: sortField === 'code' ? sortOrder : null,
    },
    {
      title: t('storeGroup.name'),
      dataIndex: 'name',
      key: 'name',
      // sorter: true,
      // sortOrder: sortField === 'name' ? sortOrder : null,
    },
    {
      title: t('storeGroup.storeCount'),
      dataIndex: 'shopCodes',
      key: 'shopCodes',
      // sorter: true,
      // sortOrder: sortField === 'shopCodes' ? sortOrder : null,
      render: (shopCodes: string[]) => shopCodes?.length || 0,
    },
    {
      title: t('common.division'),
      dataIndex: 'mainDivisionName',
      key: 'mainDivisionName',
    },
    {
      title: t('storeGroup.displayGroup'),
      dataIndex: 'displayGroupCode',
      key: 'displayGroupCode',
    },
    {
      title: t('storeGroup.displayGroupName'),
      dataIndex: 'displayGroupName',
      key: 'displayGroupName',
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: StoreGroupStatus) => {
        const statusKey = status === 'ENABLE' ? 'enable' : 'disable';
        return (
          <Tag color={STORE_GROUP_STATUS_COLORS[status]}>
            {t(`storeGroup.status.${statusKey}`)}
          </Tag>
        );
      },
      // sorter: true,
      // sortOrder: sortField === 'status' ? sortOrder : null,
    },
    {
      title: t('common.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      // sorter: true,
      // sortOrder: sortField === 'gmtCreate' ? sortOrder : null,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('storeGroup.planogramStatus'),
      dataIndex: 'planogramStatus',
      key: 'planogramStatus',
      width: 120,
      render: (planogramStatus) => (
        <Tag color={STATUS_COLORS[planogramStatus]}>
          {getStatusTranslation(planogramStatus, t)}
        </Tag>
      ),
    },
    {
      title: t('storeGroup.commercialApprovalStatus'),
      dataIndex: 'commercialApproverStatus',
      key: 'commercialApproverStatus',
      render: (status: string) => {
        if (!status) return '-';
        const statusColors = {
          'APPROVED': 'green',
          'PENDING': 'orange',
          'REJECTED': 'red'
        };
        const statusLabels = {
          'APPROVED': t('storeGroup.approvalStatus.approved'),
          'PENDING': t('storeGroup.approvalStatus.pending'),
          'REJECTED': t('storeGroup.approvalStatus.rejected')
        };
        return (
          <Tag color={statusColors[status as keyof typeof statusColors]}>
            {statusLabels[status as keyof typeof statusLabels] || status}
          </Tag>
        );
      },
      width: 120,
      align: 'center' as const,
    },
    {
      title: t('storeGroup.planogramApprovalStatus'),
      dataIndex: 'planogramApproverStatus',
      key: 'planogramApproverStatus',
      render: (status: string) => {
        if (!status) return '-';
        const statusColors = {
          'APPROVED': 'green',
          'PENDING': 'orange',
          'REJECTED': 'red'
        };
        const statusLabels = {
          'APPROVED': t('storeGroup.approvalStatus.approved'),
          'PENDING': t('storeGroup.approvalStatus.pending'),
          'REJECTED': t('storeGroup.approvalStatus.rejected')
        };
        return (
          <Tag color={statusColors[status as keyof typeof statusColors]}>
            {statusLabels[status as keyof typeof statusLabels] || status}
          </Tag>
        );
      },
      width: 120,
      align: 'center' as const,
    },
    {
      title: t('storeGroup.operateApprovalStatus'),
      dataIndex: 'operateApproverStatus',
      key: 'operateApproverStatus',
      render: (status: string) => {
        if (!status) return '-';
        const statusColors = {
          'APPROVED': 'green',
          'PENDING': 'orange',
          'REJECTED': 'red'
        };
        const statusLabels = {
          'APPROVED': t('storeGroup.approvalStatus.approved'),
          'PENDING': t('storeGroup.approvalStatus.pending'),
          'REJECTED': t('storeGroup.approvalStatus.rejected')
        };
        return (
          <Tag color={statusColors[status as keyof typeof statusColors]}>
            {statusLabels[status as keyof typeof statusLabels] || status}
          </Tag>
        );
      },
      width: 120,
      align: 'center' as const,
    },
    // {
    //   title: t('storeGroup.lastExportTime'),
    //   dataIndex: 'lastExportTime',
    //   key: 'lastExportTime',
    //   render: (timestamp: number) => formatDateTime(timestamp),
    // },
    {
      title: t('storeGroup.lastImportTime'),
      dataIndex: 'lastImportTime',
      key: 'lastImportTime',
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: StoreGroup) => (
        <Space size="small">
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title={t('storeGroup.benchmarkViewTooltip')}>
            <Button
              type="text"
              size="small"
              icon={<AppstoreOutlined />}
              onClick={() => handleBenchmarkView(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
            />
          </Dropdown>
        </Space>
      ),
    },
  ];

   const formatDateTime = (timestamp: number): string => {
    if (!timestamp) {
      return '-';
    }
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };
  
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  return (
    <div className="store-group-list">
      <FilterPanel
        form={form}
        onFilter={handleFiltersChange}
        onReset={handleReset}
        title={t('storeGroup.title')}
        className="filter-card"
      >        
        <Col span={6}>
          <Form.Item name="status" label={t('common.status')}>
            <Select placeholder={t('storeGroupFilter.selectStatus')} allowClear>
              <Option key="all" value="all">
                {t('storeGroup.status.all')}
              </Option>
              <Option key="ENABLE" value="ENABLE">
                {t('storeGroup.status.enable')}
              </Option>
              <Option key="DISABLE" value="DISABLE">
                {t('storeGroup.status.disable')}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="mainDivisionCode" label={t('common.division')}>
            <Select
              placeholder={t('common.division')}
              allowClear
              loading={divisionsLoading}
              showSearch
              optionFilterProp="children"
            >
              {divisions.map((division) => (
                <Option key={division.value} value={division.value}>
                  {division.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="displayGroupCode" label={t('storeGroupFilter.relatedDisplayGroup')}>
            <Select
              placeholder={t('storeGroupFilter.selectDisplayGroup')}
              allowClear
              loading={displayGroupsLoading}
              showSearch
              optionFilterProp="children"
            >
              {displayGroups.map((group) => (
                <Option key={group.code} value={group.code}>
                  {`${group.code} - ${group.name}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="keyword" label={t('storeGroupFilter.keyword')}>
            <Input placeholder={t('storeGroupFilter.keywordPlaceholder')} allowClear />
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card className="table-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <BatchActionToolbar
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: 'activate',
                label: t('common.batchActivate'),
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                onClick: () => handleBatchStatusChange('ENABLE'),
                disabled: loading
              },
              {
                key: 'deactivate',
                label: t('common.batchDeactivate'),
                icon: <StopOutlined style={{ color: '#faad14' }} />,
                onClick: () => handleBatchStatusChange('DISABLE'),
                disabled: loading
              }
            ]}
            onClearSelection={() => setSelectedRowKeys([])}
          />
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('storeGroup.create')}
            </Button>
          </Space>
        </div>

        {storeGroups.length > 0 ? (
           <Table
             rowKey="code"
             rowSelection={rowSelection}
             columns={columns}
             dataSource={storeGroups}
             loading={loading}
             pagination={{
               current,
               pageSize,
               total,
               showSizeChanger: true,
               showQuickJumper: true,
               showTotal: (total) => t('common.paginationTotal', { total }),
             }}
             onChange={handleTableChange}
              scroll={{ x: 1800, y: 500 }}
              size="middle"
           />
         ) : (
           <Empty
             image={Empty.PRESENTED_IMAGE_SIMPLE}
             description={
               <span>
                 {Object.keys(filters).some(key => 
                   filters[key as keyof typeof filters] !== 'all' && 
                   filters[key as keyof typeof filters] !== '' && 
                   filters[key as keyof typeof filters] !== undefined
                 )
                   ? t('storeGroup.noMatchingData')
                   : t('storeGroup.noData')}
               </span>
             }
             style={{ margin: '40px 0' }}
           />
         )}
      </Card>
    </div>
  );
};

export default StoreGroupList;